import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utils/axiosInstance';
import { loginApi, API, getAuthHeaders } from '../config/api';

const tenantUserApi = (userId = '') => {
  const base = `${API.login}/api/v1/admin/tenants/users`;
  return userId ? `${base}/${userId}` : base;
};

export const fetchTenantUsers = createAsyncThunk(
  'adminTenants/fetchUsers',
  async (tenantId, { rejectWithValue, getState }) => {
    try {
      if (!tenantId) throw new Error('Tenant ID not found');
      const response = await axios.get(loginApi(`/user/tenant/${tenantId}`), {
        headers: getAuthHeaders(getState),
      });
      const users = Array.isArray(response.data)
        ? response.data
        : response.data?.data ?? [];
      return users.map((u) => ({
        userName:
          [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        department: u.department || '—',
        email: u.email,
        userRole: u.userRole ?? u.role ?? 'user',
        lastActivity: u.lastActivity ?? 'N/A',
        userId: u.userId ?? u.buyerId,
        phone: u.phone ?? '',
        jobTitle: u.jobTitle ?? '',
        status: u.status ?? 'Active',
      }));
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? 'Failed to load tenant users',
      );
    }
  },
);

export const updateTenantUser = createAsyncThunk(
  'adminTenants/updateUser',
  async ({ userId, updates }, { rejectWithValue, getState }) => {
    try {
      if (!userId) throw new Error('User ID is required');
      const response = await axios.put(
        tenantUserApi(userId),
        updates,
        { headers: getAuthHeaders(getState) },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ??
          error.response?.data ??
          'Failed to update user',
      );
    }
  },
);

export const removeTenantUser = createAsyncThunk(
  'adminTenants/removeUser',
  async (userId, { rejectWithValue, getState }) => {
    try {
      if (!userId) throw new Error('User ID is required');
      const response = await axios.delete(tenantUserApi(userId), {
        headers: getAuthHeaders(getState),
      });
      return { userId, ...(response.data ?? {}) };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ??
          error.response?.data ??
          'Failed to remove user',
      );
    }
  },
);

const adminTenantsSlice = createSlice({
  name: 'adminTenants',
  initialState: {
    users: [],
    loading: false,
    error: null,
    mutating: false,
    mutateError: null,
  },
  reducers: {
    clearMutateError: (state) => {
      state.mutateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenantUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenantUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchTenantUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTenantUser.pending, (state) => {
        state.mutating = true;
        state.mutateError = null;
      })
      .addCase(updateTenantUser.fulfilled, (state) => {
        state.mutating = false;
      })
      .addCase(updateTenantUser.rejected, (state, action) => {
        state.mutating = false;
        state.mutateError = action.payload;
      })
      .addCase(removeTenantUser.pending, (state) => {
        state.mutating = true;
        state.mutateError = null;
      })
      .addCase(removeTenantUser.fulfilled, (state, action) => {
        state.mutating = false;
        state.users = state.users.filter(
          (u) => u.userId !== action.payload.userId,
        );
      })
      .addCase(removeTenantUser.rejected, (state, action) => {
        state.mutating = false;
        state.mutateError = action.payload;
      });
  },
});

export default adminTenantsSlice.reducer;
export const { clearMutateError } = adminTenantsSlice.actions;
export const selectTenantUsers = (s) => s.adminTenants.users;
export const selectTenantUsersLoading = (s) => s.adminTenants.loading;
export const selectTenantUsersError = (s) => s.adminTenants.error;
export const selectTenantMutating = (s) => s.adminTenants.mutating;
export const selectTenantMutateError = (s) => s.adminTenants.mutateError;
